import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import gql from 'graphql-tag';
import Loader from 'react-loading';
import Honeybadger from '@honeybadger-io/js';
import { connect } from 'react-redux';
import { useQuery } from '@apollo/react-hooks';
import { themes, ConversationType, ReduxState } from 'kb-shared';
import { FEATURE_INITIATE_MESSAGES } from 'config/features';
import { RouteComponentProps } from 'react-router-dom';
import qs from 'qs';

import { Title } from '../styled_common';
import { AbsoluteCentered } from '../../styled';
import ActiveConversation from './Conversation';
import NoMessages from './NoMessages';
import ConversationPreview from './ConversationPreview';
import ConversationAdjust from './ConversationAdjust';
import MessageCategorySelector from './MessageCategorySelector';
import NewConversationPreview from './NewConversationPreview';
import { YellowButton } from '../../components/Buttons';
import ComposeNewMessage from './ComposeNewMessage';

const CONVERSATIONS = gql`
  query conversations {
    conversations {
      id
      patientId
      category
      subject
      status
      assignedToUserName
      assignedToUserRole
      assignedToUserPhotoUrl
      preview
      unreadCount
      lastMessageTimestamp
    }
  }
`;

type MessagesProps = RouteComponentProps & {
  initiateMessagesFeatureEnabled?: string | boolean;
};

const Messages = ({ location, initiateMessagesFeatureEnabled }: MessagesProps) => {
  const [activeConversationIdx, setActiveConversationIdx] = useState(0);
  const [showCategorySelector, setShowCategorySelector] = useState(false);
  const [switchingConvo, setSwitchingConvo] = useState(false);
  const [category, setCategory] = useState<string | undefined>(undefined);
  const { loading, data, error, refetch } = useQuery(CONVERSATIONS, {
    fetchPolicy: 'cache-and-network'
  });
  const [conversationToShowsMobile, setConversationsToShow] = useState({
    min: 0
  });
  const [composeMessageUrlParams, setComposeMessageUrlParams] = useState({
    subject: '',
    message: '',
    category: ''
  });
  const [initializedMessageUrl, setInitializedMessageUrl] = useState(false);

  const isMobileViewSize = () => window.innerWidth <= 600;
  const isTabletViewSize = () => window.innerWidth < 1084;
  const [isMobileView, setisMobileView] = useState(isMobileViewSize());
  const [isTabletView, setIsTabletView] = useState(isTabletViewSize());
  const composingNewMessage = activeConversationIdx === -1;

  function moveConversations(adjustPage: number) {
    const min = Math.max(conversationToShowsMobile.min + adjustPage, 0);
    setConversationsToShow({ min });
  }

  useEffect(() => {
    if (window.analytics) {
      window.analytics.page('Messages');
    }
  }, []);

  useEffect(() => {
    if (!initializedMessageUrl) {
      const params = qs.parse(location.search.replace('?', '')) as any;
      if (params.category) {
        setCategory(params.category);
        setComposeMessageUrlParams(params);
        setActiveConversationIdx(-1);
      }
      setInitializedMessageUrl(true);
    }

    function handleResize() {
      setisMobileView(isMobileViewSize());
      setIsTabletView(isTabletViewSize());
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [location.search, setInitializedMessageUrl, initializedMessageUrl]);

  if (error) {
    Honeybadger.notify(error, { name: 'ConversationsQueryError' });
    console.error(error);
  }

  if (!data) {
    return (
      <Container>
        <Title>Messages</Title>
        <AbsoluteCentered>
          <Loader type="spin" color={themes.colors.KBYellow} height={150} width={150} />
        </AbsoluteCentered>
      </Container>
    );
  }

  const { conversations } = data;

  if (conversations.length < 1 && !composingNewMessage) {
    return (
      <Container>
        <Title>Messages</Title>
        <NoMessages>
          {initiateMessagesFeatureEnabled ? (
            <ComposeButtonContainer>
              <YellowButton
                onClick={() => {
                  setShowCategorySelector(true);
                }}
              >
                &#43;&nbsp;Compose
              </YellowButton>
              {showCategorySelector && (
                <MessageCategorySelector
                  onClose={() => setShowCategorySelector(false)}
                  onSelect={category => {
                    setCategory(category);
                    setShowCategorySelector(false);
                    setActiveConversationIdx(-1);
                  }}
                />
              )}
            </ComposeButtonContainer>
          ) : (
            <YellowButton onClick={() => (window.location.href = '/')}>
              Go to dashboard
            </YellowButton>
          )}
        </NoMessages>
      </Container>
    );
  }

  const activeConversation = conversations[activeConversationIdx];
  const unreadConvoCount = conversations.reduce((total: number, conversation: ConversationType) => {
    return total + (conversation.unreadCount > 0 ? 1 : 0);
  }, 0);
  return (
    <Container>
      <HeaderContainer>
        <Title>
          Messages{' '}
          {(isMobileView || isTabletView) && unreadConvoCount > 0 && (
            <MessageCountBadge> {unreadConvoCount} New</MessageCountBadge>
          )}
        </Title>
        {initiateMessagesFeatureEnabled && (
          <ComposeButtonContainer>
            <YellowButton
              onClick={() => {
                setShowCategorySelector(true);
              }}
            >
              &#43;&nbsp;Compose
            </YellowButton>
            {showCategorySelector && (
              <MessageCategorySelector
                onClose={() => setShowCategorySelector(false)}
                onSelect={category => {
                  setCategory(category);
                  setShowCategorySelector(false);
                  setActiveConversationIdx(-1);
                }}
              />
            )}
          </ComposeButtonContainer>
        )}
      </HeaderContainer>
      <Main>
        <ConversationsList>
          {!activeConversation && (
            <NewConversationPreview
              onClick={() => {
                setActiveConversationIdx(-1);
              }}
            />
          )}
          {isMobileView && conversationToShowsMobile.min !== 0 && (
            <ConversationAdjust increase={false} onClick={() => moveConversations(-1)} />
          )}

          {conversations.map(
            (conversation: ConversationType, idx: number) =>
              (!isMobileView ||
                (isMobileView &&
                  conversationToShowsMobile.min <= idx &&
                  conversationToShowsMobile.min + 5 > idx)) && (
                <ConversationPreview
                  key={conversation.id}
                  conversation={conversation}
                  idx={idx}
                  active={activeConversationIdx === idx}
                  onClick={idx => {
                    setActiveConversationIdx(idx);
                    setSwitchingConvo(true);
                    setTimeout(() => setSwitchingConvo(false), 10);
                  }}
                />
              )
          )}
          {isMobileView && conversationToShowsMobile.min + 5 < conversations.length && (
            <ConversationAdjust increase={true} onClick={() => moveConversations(1)} />
          )}
        </ConversationsList>
        {activeConversation && !loading && !switchingConvo ? (
          <ActiveConversation conversationId={activeConversation.id} />
        ) : (
          <ComposeNewMessage
            initialComposition={composeMessageUrlParams}
            category={category}
            afterSend={() => {
              setTimeout(() => {
                refetch()
                  .then(() => setActiveConversationIdx(0))
                  .catch(error => Honeybadger.notify(error));
              }, 100);
            }}
          />
        )}
      </Main>
    </Container>
  );
};

const mapStateToProps = (state: ReduxState) => ({
  initiateMessagesFeatureEnabled: state.features.features[FEATURE_INITIATE_MESSAGES]
});

export default connect(mapStateToProps)(Messages);

const Container = styled.div`
  ${({ theme }) => theme.grid.pageContainer};
  margin-top: 100px;
`;

const Main = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints.tabletPortrait}) {
    display: grid;
    grid-template-columns: 33% auto;
    grid-column-gap: 4px;
  }
`;

const ConversationsList = styled.ul`
  ${({ theme }) => theme.fonts.FoundersGrotesk};

  @media ${({ theme }) => theme.queries.mobile} {
    display: flex;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;

  @media (max-width: ${({ theme }) => theme.breakpoints.tabletPortrait}) {
    display: grid;
    justify-content: flex-start;
    margin-bottom: 10px;
  }

  button {
    height: min-content;
  }
`;

const MessageCountBadge = styled.span`
  background-color: ${({ theme }) => theme.colors.yellow};
  margin-left: 5px;
  padding: 2px 5px;
  font-size: 16pt;
`;

const ComposeButtonContainer = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints.tabletPortrait}) {
    position: relative;
  }
`;
